<template>
 <div class="lesson-card card transition-shadow duration-200 cursor-pointer"
 @click="onCardClick"
 :data-lesson-id="lesson.id"
 :data-focus-target="lesson.is_focus_target"
 :class="{
 'border-4 border-red-700 dark:border-red-400 !bg-red-100 dark:!bg-red-900/30 animate-pulse-border': needsAttendanceFlag,
 'attended-highlight': showAttendedHighlight
 }">

 <!-- Header: title + first student on the left, date/time on the right -->
 <div class="flex items-start justify-between">
 <div class="flex-1 min-w-0">

 <!-- Lesson title + duration -->
 <div class="lesson-title-row flex items-baseline mb-1 min-w-0">
 <button @click="showDetailsModal = true" :data-tour="(!isUnavailable && isEditable) ? 'lesson-title' : null"
 class="lc-title-full font-semibold text-fg-base dark:text-fg-base-dark truncate min-w-0 mr-2 text-left transition-colors">
 {{ lesson.lesson_title }}
 </button>
 <button @click="showDetailsModal = true" :data-tour="(!isUnavailable && isEditable) ? 'lesson-title' : null"
 class="lc-title-short font-semibold text-fg-base dark:text-fg-base-dark truncate min-w-0 mr-2 text-left transition-colors">
 {{ titleAbbreviated }}
 </button>
 <span class="lc-duration-full text-xs text-fg-muted dark:text-fg-muted-dark flex-shrink-0 whitespace-nowrap">
 {{ lesson.duration_minutes }} min • {{ lesson.instrument }}
 </span>
 <span class="lc-duration-abbrev text-xs text-fg-muted dark:text-fg-muted-dark flex-shrink-0 whitespace-nowrap">
 {{ durationAbbreviated }} • {{ lesson.instrument }}
 </span>
 <span class="lc-duration-noinstrument text-xs text-fg-muted dark:text-fg-muted-dark flex-shrink-0 whitespace-nowrap">
 {{ durationAbbreviated }}
 </span>
 </div>

 <!-- First student row sits in the left column, level with the relative time -->
 <div v-if="studentsData.length" class="mt-1 text-sm flex items-center min-w-0">
 <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mr-2 flex-shrink-0"
 :class="getStudentStatusClass(studentsData[0].attendance)">
 {{ formatStudentStatus(studentsData[0].attendance) }}
 </span>
 <span v-if="lesson.teacher_cancellation_id"
 title="Excused because the teacher marked this time unavailable"
 class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mr-2 flex-shrink-0 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
 🚫 Unavailable
 </span>
 <span class="text-fg-secondary dark:text-fg-secondary-dark truncate min-w-0 flex-shrink-1">{{ studentsData[0].name }}</span>
 <div v-if="isStudentRecorded(studentsData[0].attendance)" class="ml-2 flex items-center space-x-2 flex-shrink-0">
 <button v-if="isEditable" @click="startEditStudent(studentsData[0].name)"
 class="text-xs text-blue-600 dark:text-blue-400 underline">
 Edit
 </button>
 <button v-if="isFuture" @click="removeStudentAttendance(studentsData[0].name)"
 class="text-xs text-red-600 dark:text-red-400 underline">
 Remove
 </button>
 </div>
 </div>

 </div>

 <!-- Time indicator -->
 <div class="flex flex-col items-end flex-shrink-0 ml-3">
 <div class="flex items-center gap-1.5">
 <div class="text-sm font-medium"
 :class="isFuture ? 'text-fg-base dark:text-fg-base-dark' : 'text-fg-secondary dark:text-fg-muted-dark'">
 {{ formatDate(lesson.lesson_date) }} • {{ lesson.lesson_time }}
 </div>
 </div>
 <div class="text-xs text-fg-muted dark:text-fg-muted-dark mt-1 flex items-center gap-1">
 {{ relativeTime }}
 <PaperClipIcon v-if="homeworkStatus !== 'none'" class="h-3.5 w-3.5"
 :class="homeworkStatus === 'sent' ? 'text-green-600 dark:text-green-400' : 'text-amber-500 dark:text-amber-400'"
 :title="homeworkStatus === 'sent' ? 'Homework sent' : 'Homework draft — not sent yet'" />
 </div>
 </div>
 </div>

 <!-- First student's buttons: compact outline pill, floated right for thumb reach -->
 <div v-if="studentsData.length && showStudentButtons(studentsData[0].name, studentsData[0].attendance)"
 class="flex justify-end mt-2"
 data-attendance-buttons
 :data-student-name="studentsData[0].name">
 <AttendanceButtonPill
 :status="studentsData[0].attendance"
 :is-editing="isStudentEditing(studentsData[0].name)"
 :pulse-status="clickedButton?.studentName === studentsData[0].name ? 'attended' : null"
 :available-statuses="availableAttendanceStatuses"
 @select="status => handleStudentAttendance(status, studentsData[0].name)"
 @pulse-end="clickedButton = null"
 />
 </div>

 <!-- Remaining students: name row then outline pill, interleaved -->
 <template v-for="student in studentsData.slice(1)" :key="student.name">
 <div class="mt-2 text-sm flex items-center mb-2 min-w-0">
 <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mr-2 flex-shrink-0"
 :class="getStudentStatusClass(student.attendance)">
 {{ formatStudentStatus(student.attendance) }}
 </span>
 <span class="text-fg-secondary dark:text-fg-secondary-dark truncate min-w-0 flex-shrink-1">{{ student.name }}</span>
 <div v-if="isStudentRecorded(student.attendance)" class="ml-2 flex items-center space-x-2 flex-shrink-0">
 <button v-if="isEditable" @click="startEditStudent(student.name)"
 class="text-xs text-blue-600 dark:text-blue-400 underline">
 Edit
 </button>
 <button v-if="isFuture" @click="removeStudentAttendance(student.name)"
 class="text-xs text-red-600 dark:text-red-400 underline">
 Remove
 </button>
 </div>
 </div>
 <div v-if="showStudentButtons(student.name, student.attendance)"
 class="flex justify-end"
 data-attendance-buttons
 :data-student-name="student.name">
 <AttendanceButtonPill
 :status="student.attendance"
 :is-editing="isStudentEditing(student.name)"
 :pulse-status="clickedButton?.studentName === student.name ? 'attended' : null"
 :available-statuses="availableAttendanceStatuses"
 @select="status => handleStudentAttendance(status, student.name)"
 @pulse-end="clickedButton = null"
 />
 </div>
 </template>

 <!-- Lesson Details Modal -->
 <LessonDetailsModal
 v-if="showDetailsModal"
 :lesson="lesson"
 :isEditable="!isUnavailable && isEditable"
 @close="showDetailsModal = false"
 @edit="showDetailsModal = false; showEditModal = true"
 />

 <!-- Lesson Edit Modal -->
 <LessonEditModal
 v-if="showEditModal"
 :lesson="lesson"
 @close="showEditModal = false"
 @saved="applyEdit"
 />

 </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useApi } from '../../composables/useApi'
import { useDemoMode } from '../../composables/useDemoMode'
import { lessonLockTier, notifyLessonsChanged } from '../../composables/useCalendarData.js'
import LessonEditModal from './LessonEditModal.vue'
import LessonDetailsModal from './LessonDetailsModal.vue'
import AttendanceButtonPill from './AttendanceButtonPill.vue'
import { PaperClipIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
 lesson: { type: Object, required: true },
 isFuture: { type: Boolean, default: false }
})

const emit = defineEmits(['mark-attended', 'mark-excused', 'mark-absent', 'mark-not-reported', 'revert-attendance'])

const { markLessonAttended, markLessonExcused, markLessonAbsent, markLessonNotReported } = useApi()

// Per-student local status overrides: { [studentName]: status }
// Spread-replaced on each write to maintain Vue reactivity.
const localStatuses = ref({})

// Per-student editing state: { [studentName]: true }
const editingStudents = ref({})

// UI state
const showDetailsModal = ref(false)
const showEditModal = ref(false)
const clickedButton = ref(null) // { studentName } while attended-pop animation is active
const showAttendedHighlight = ref(false)
// Status only — homework is opened from the details popup (LessonDetailsModal), which
// writes lesson.homework_status back so this icon updates without a refetch.
const homeworkStatus = computed(() => props.lesson.homework_status ?? 'none')

// Tapping the card anywhere except its controls opens the details popup.
const onCardClick = (e) => {
 if (e.target.closest('button, a, input, select, textarea, label, [data-attendance-buttons]')) return
 showDetailsModal.value = true
}

// ── Per-student helpers ─────────────────────────────────────────────────────

const isStudentRecorded = (status) => ['attended', 'excused', 'absent'].includes(status)
const isStudentEditing = (studentName) => !!editingStudents.value[studentName]
const showStudentButtons = (studentName, status) =>
 !isUnavailable.value && (!isStudentRecorded(status) || isStudentEditing(studentName))

// ── Derived state ───────────────────────────────────────────────────────────

const studentsData = computed(() => {
 if (Array.isArray(props.lesson.attendance)) {
 return props.lesson.attendance.map(s => ({
 ...s,
 attendance: localStatuses.value[s.name] ?? s.status ?? s.attendance ?? 'not reported'
 }))
 }
 // Fallback for lessons that have no attendance array
 const name = props.lesson.lesson_title || 'Student'
 return [{ name, attendance: localStatuses.value[name] ?? props.lesson.attendance_status ?? 'not reported' }]
})

// Lessons with an empty attendance array are unavailable placeholder slots.
const isUnavailable = computed(() =>
 Array.isArray(props.lesson.attendance) && props.lesson.attendance.length === 0
)

// Narrow-card fallback for the title: "First W." — swapped in via container query, see <style>.
const titleAbbreviated = computed(() => {
 const words = (props.lesson.lesson_title || '').trim().split(/\s+/)
 return words.length < 2 ? words[0] ?? '' : `${words[0]} ${words[1][0]}.`
})

const durationAbbreviated = computed(() => `${props.lesson.duration_minutes}m`)

const effectivePermissions = useDemoMode().effectivePermissionsFor(() => {
 const user = JSON.parse(localStorage.getItem('user') || 'null')
 return user?.permissions
})
const isAdmin = computed(() => effectivePermissions.value.includes('admin'))

// 'paid' locks out everyone (even admins); 'locked' (dispute window closed,
// nothing charged yet) is admin-overridable; 'open' is unrestricted.
const isEditable = computed(() => {
 const tier = lessonLockTier(props.lesson)
 if (tier === 'paid') return false
 if (tier === 'locked') return isAdmin.value
 return true
})

// Card pulses red when ANY student on a past lesson is still unreported.
const needsAttendanceFlag = computed(() =>
 !props.isFuture && !isUnavailable.value &&
 studentsData.value.some(s => !isStudentRecorded(s.attendance))
)



// ── Date/time helpers ───────────────────────────────────────────────────────

// Parse a date-only string (YYYY-MM-DD) as local midnight to avoid UTC-shift bugs.
const parseLocalDate = (dateString) => {
 if (!dateString) return new Date(NaN)
 return /^\d{4}-\d{2}-\d{2}$/.test(dateString)
 ? new Date(dateString + 'T00:00:00')
 : new Date(dateString)
}

// Mirrors backend guard (lessons.py _lesson_is_future): attended/absent are rejected
// when the lesson's date is more than 24h out, measured from lesson midnight.
const isMoreThan24HoursOut = computed(() =>
 parseLocalDate(props.lesson.lesson_date).getTime() > Date.now() + 24 * 60 * 60 * 1000
)

const availableAttendanceStatuses = computed(() =>
 isMoreThan24HoursOut.value ? ['excused'] : ['absent', 'excused', 'attended']
)

const relativeTime = computed(() => {
 const lessonDate = parseLocalDate(props.lesson.lesson_date)
 const today = new Date()
 const lessonMidnight = new Date(lessonDate.getFullYear(), lessonDate.getMonth(), lessonDate.getDate())
 const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate())
 const diffDays = Math.round((lessonMidnight - todayMidnight) / (1000 * 60 * 60 * 24))
 const dys = Math.abs(diffDays)
 if (diffDays === 0) return 'Today'
 return diffDays > 0 ? `in ${dys} day${dys !== 1 ? 's' : ''}` : `${dys} day${dys !== 1 ? 's' : ''} ago`
})

const formatDate = (dateString) => {
 const date = parseLocalDate(dateString)
 const today = new Date()
 const tomorrow = new Date(today)
 tomorrow.setDate(tomorrow.getDate() + 1)
 if (date.toDateString() === today.toDateString()) return 'Today'
 if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
 return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const formatStudentStatus = (status) => {
 if (!status || status === 'not reported' || status === 'scheduled') return props.isFuture ? 'Scheduled' : 'Not Reported'
 return { attended: 'Attended', excused: 'Excused', absent: 'Absent' }[status]
 ?? (status.charAt(0).toUpperCase() + status.slice(1))
}

const getStudentStatusClass = (status) => {
 if (status === 'not reported' || status === 'scheduled') {
 return props.isFuture
 ? 'bg-blue-500 dark:bg-blue-700 text-white'
 : 'bg-red-600 dark:bg-red-700 text-white'
 }
 return {
 attended: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
 excused: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
 absent: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
 missing: 'bg-red-600 dark:bg-red-700 text-white'
 }[status] ?? 'bg-surface-muted dark:bg-surface-dark text-fg-base dark:text-fg-base-dark'
}

// ── API ─────────────────────────────────────────────────────────────────────

const callApi = async (status, studentName) => {
 const apiMap = {
 attended: () => markLessonAttended(props.lesson.id, studentName),
 excused: () => markLessonExcused(props.lesson.id, studentName),
 absent: () => markLessonAbsent(props.lesson.id, studentName),
 remove: () => markLessonNotReported(props.lesson.id, studentName)
 }
 try {
 await apiMap[status]()
 notifyLessonsChanged({ attendanceOnly: true })
 return { success: true }
 } catch (err) {
 console.error('Attendance update failed:', err)
 return { success: false }
 }
}

// ── Attendance actions ──────────────────────────────────────────────────────

const getPreviousStatus = (studentName) => {
 if (localStatuses.value[studentName]) return localStatuses.value[studentName]
 const s = props.lesson.attendance?.find(a => a.name === studentName)
 return s?.status ?? s?.attendance ?? 'not reported'
}

const applyAttendance = async (status, studentName) => {
 const previousStatus = getPreviousStatus(studentName)

 // Optimistic update
 localStatuses.value = { ...localStatuses.value, [studentName]: status }

 // Clear editing state for this student
 const { [studentName]: _removed, ...remainingEditing } = editingStudents.value
 editingStudents.value = remainingEditing

 const emitMap = { attended: 'mark-attended', excused: 'mark-excused', absent: 'mark-absent' }
 emit(emitMap[status], props.lesson.id, studentName)

 if (status === 'attended') {
 showAttendedHighlight.value = true
 setTimeout(() => { showAttendedHighlight.value = false }, 1000)
 }

 const { success } = await callApi(status, studentName)
 if (!success) {
 localStatuses.value = { ...localStatuses.value, [studentName]: previousStatus }
 emit('revert-attendance', props.lesson.id, studentName, previousStatus)
 }
}

const handleStudentAttendance = (status, studentName) => {
 if (status === 'attended') clickedButton.value = { studentName }
 applyAttendance(status, studentName)
}

const startEditStudent = (studentName) => {
 if (!isEditable.value) return
 editingStudents.value = { ...editingStudents.value, [studentName]: true }
}

const removeStudentAttendance = async (studentName) => {
 const previousStatus = getPreviousStatus(studentName)
 localStatuses.value = { ...localStatuses.value, [studentName]: 'not reported' }
 emit('mark-not-reported', props.lesson.id, studentName)
 const { success } = await callApi('remove', studentName)
 if (!success) {
 localStatuses.value = { ...localStatuses.value, [studentName]: previousStatus }
 emit('revert-attendance', props.lesson.id, studentName, previousStatus)
 }
}

const applyEdit = (updatedLesson) => {
 showEditModal.value = false
 if (!updatedLesson) return
 if (updatedLesson.lesson_time !== undefined) props.lesson.lesson_time = updatedLesson.lesson_time
 if (updatedLesson.lesson_date !== undefined) props.lesson.lesson_date = updatedLesson.lesson_date
 // duration_minutes/price_cents/location were dropped here — the card kept showing
 // the pre-edit duration even after a successful save, which is what made a
 // duration/price edit look like it hadn't taken effect. Confirmed as the real
 // trigger behind the 2026-09-14 Dan Wall/Schilling price-compounding incident: a
 // teacher/admin, seeing no visible change, re-submitted the same duration change
 // two more times, and each resubmission re-triggered the backend's re-pricing
 // logic (see series_editing.py's duration_minutes cascade fix, same incident).
 if (updatedLesson.duration_minutes !== undefined) props.lesson.duration_minutes = updatedLesson.duration_minutes
 if (updatedLesson.price_cents !== undefined) props.lesson.price_cents = updatedLesson.price_cents
 if (updatedLesson.location !== undefined) props.lesson.location = updatedLesson.location
}
</script>

<style scoped>
.lesson-card {
 @apply border border-card-border dark:border-card-border-dark;
}

/* Title/duration row: progressively truncate instead of wrapping to a 2nd line
   as the card narrows. See titleAbbreviated/durationAbbreviated in <script>. */
.lesson-title-row {
 container-type: inline-size;
 container-name: lesson-title-row;
}

.lc-title-short,
.lc-duration-abbrev,
.lc-duration-noinstrument {
 display: none;
}

/* Stage 1: "Emma Wilson" -> "Emma W." */
@container lesson-title-row (max-width: 230px) {
 .lc-title-full { display: none; }
 .lc-title-short { display: inline; }
}

/* Stage 2: "30 min • Piano" -> "30m • Piano" */
@container lesson-title-row (max-width: 190px) {
 .lc-duration-full { display: none; }
 .lc-duration-abbrev { display: inline; }
}

/* Stage 3: drop the instrument entirely -> "30m" */
@container lesson-title-row (max-width: 150px) {
 .lc-duration-abbrev { display: none; }
 .lc-duration-noinstrument { display: inline; }
}

@keyframes pulse-border {
 0%, 100% { border-color: rgba(185, 28, 28, 1); }
 50% { border-color: rgba(185, 28, 28, 0.7); }
}
.animate-pulse-border {
 animation: pulse-border 2s infinite;
}

@keyframes attended-highlight {
 0% { background-color: rgba(34, 197, 94, 0.1); }
 100% { background-color: transparent; }
}
.attended-highlight {
 animation: attended-highlight 1s ease-out;
}
</style>
